import { useCallback, useState } from "react";

import { usePostHog } from "posthog-js/react";

import { extractAPIRequestErrorMessage } from "utils";

import { useAPI } from "./useAPI";
import { useError } from "./useError";
import { useLoading } from "./useLoading";
import { useRoleContext } from "./useRoleContext";
import { ClientAppEvent } from "../constants";

import type { InviteTargetType } from "../components/SupplierNetwork/types";

export function useRespondToNetworkInvite() {
  const { api } = useAPI();
  const { role } = useRoleContext();
  const posthog = usePostHog();

  if (role && role.type !== "SHIPPER") {
    throw new Error(
      "useRespondToNetworkInvite can only be used by shipper users",
    );
  }
  const { error, setError } = useError();
  const { loading, setLoading } = useLoading(false);
  const [success, setSuccess] = useState<boolean>(false);

  const resetState = () => {
    setError(null);
    setLoading(false);
    setSuccess(false);
  };

  const respondToInvite = useCallback(
    (
      inviteId: number,
      inviteTargetType: InviteTargetType,
      accepted: boolean,
    ) => {
      setError(null);
      setLoading(true);

      const url =
        inviteTargetType === "SUPPLIER"
          ? `/supplier-network-internal-invites/${inviteId}/respond`
          : `/supplier-network-internal-customer-invites/${inviteId}/respond`;
      const body = { accepted };

      api("post", url, body)
        .then(() => {
          const event =
            inviteTargetType === "SUPPLIER"
              ? ClientAppEvent.SUPPLIER_NETWORK_INVITE_RESPONDED
              : ClientAppEvent.CUSTOMER_NETWORK_INVITE_RESPONDED;
          posthog?.capture(event, { accepted });
          setSuccess(true);
          setError(null);
        })
        .catch((e) => {
          setError(
            extractAPIRequestErrorMessage(e, "Failed to respond to invite"),
          );
          setSuccess(false);
        })
        .finally(() => {
          setLoading(false);
        });
    },
    [api, setError, setLoading, posthog],
  );

  return {
    loading,
    error,
    success,
    respondToInvite,
    resetState,
  };
}
